<script lang="ts">
	import { beforeUpdate, createEventDispatcher, onMount } from 'svelte';
	import {
		toast,
		Button,
		Modal,
		Wizard,
		WizardContainer,
		WizardContent,
		WizardItem,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { edit } from 'src/stores/ui/edit_lists';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { alarms } from 'src/stores/lists/scenes-derived';
	import { isShellyPresence, real_id } from '../../../helpers/interface_helpers';
	import { scenes } from 'src/stores/lists/scenes';
	import {
		setWizardSteps,
		scrollToElement,
		resetWizardItems,
		updateWizardStepOnPrevNext,
	} from '../../Lists/list-helpers';
	import MainStep from './Steps/MainStep.svelte';
	import SelectDevicesStep from './Steps/SelectDevicesStep.svelte';
	import ActiveTimeStep from './Steps/ActiveTimeStep.svelte';
	import OverviewStep from './Steps/OverviewStep.svelte';
	import { alarm_object_prototype } from './alarm_object';
	import type { IAlarmZone } from './alarm_object';
	import { capFirstChar, _deepClone, deepCopy } from '../../../helpers/utilities';
	import { shellyId16 } from '../../../helpers/interface_helpers';
	import { postReq } from '../../../services/http';
	import MoreStep from './Steps/MoreStep.svelte';
	import SelectZonesStep from './Steps/SelectZonesStep.svelte';
	import DelaysStep from './Steps/DelaysStep.svelte';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import ExitConfirmationModal from 'src/components/ExitConfirmationModal.svelte';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import { onItemSaved, orderItemsInRoom } from 'src/helpers/add-entity-helpers';
	import { createAsyncAction } from 'src/helpers/async_action_guard';

	const dispatch = createEventDispatcher();
	const dispatchAlarmSaved = createEventDispatcher<{ 'alarm-saved': IAlarmZone }>();
	const { loading: isSaving, run: runSave } = createAsyncAction();

	export let id,
		showAddEditAlarmModal = false,
		alarm_object: IAlarmZone = {},
		delayArmActionError: string | undefined = '',
		delayActionError: string | undefined = '';
	let isActivityLogCheck: boolean;
	let showExitConfirmationModal = false;
	let exitConfirmationModalState = false;

	const snapshotAlarmObject = () => deepCopy(alarm_object);
	let clonedAlarmObject = snapshotAlarmObject();

	$: if (showAddEditAlarmModal) {
		clonedAlarmObject = snapshotAlarmObject();
	}
	let allSensorsRefs: { [key: string | number]: HTMLElement } = {};
	let selectedSensor;
	let selectedPresenceSensors: any[] = [];
	let wizardItemsContainerRef: HTMLElement = null;
	let wizardContainerRef: HTMLElement = null;
	let initiallyLoadedEditWizard = false;
	let inputAlarmMessegRef: HTMLInputElement = null;
	let modalRef: Modal = null;
	let scrolledToView = false;
	let wizardComponents = [
		SelectDevicesStep,
		SelectZonesStep,
		ActiveTimeStep,
		MoreStep, // 'Set up' step
		DelaysStep, // 'Delays' step
		MainStep, // 'Details' Step
	];
	let wizardItems = setWizardSteps([
		capFirstChar($format('tab_devices', { default: 'Devices' })),
		capFirstChar($format('tab_zones', { default: 'Zones' })),
		capFirstChar($format('active_time', { default: 'Active Time' })),
		capFirstChar($format('set_up_step', { default: 'Set up' })),
		capFirstChar($format('delays_step', { default: 'Delays' })),
		capFirstChar($format('details_step', { default: 'Details' })),
		capFirstChar($format('overview_step', { default: 'Overview' })),
	]).map((item, ind) => {
		return { ...item, component: wizardComponents[ind] };
	});

	const keyPress = (e) => {
		e.stopImmediatePropagation();
	};
	$: if (showAddEditAlarmModal) {
		window.addEventListener('keydown', keyPress);
	} else {
		window.removeEventListener('keydown', keyPress);
	}

	const previousButton = $format('button_back', { default: 'Back' });

	let activeStep = wizardItems[0];

	// True when any device in the current selection is a presence sensor.
	// Derived in SelectDevicesStep from the full selectedItems list, so it is accurate
	// even when the presence device is not selectedItems[0].
	let hasPresenceSelected = false;

	// If the user is on the Zones step and deselects the presence device, go back to Devices
	$: if (!hasPresenceSelected && activeStep?.activeIndex === 1 && showAddEditAlarmModal) {
		const result = updateWizardStepOnPrevNext('prev', wizardItems, activeStep);
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };
	}

	$: isLastStepWizard = activeStep.activeIndex === Object.keys(wizardItems).length - 1;
	$: isFirstStep = activeStep.activeIndex === 0;

	$: nextButton = isLastStepWizard
		? $format('button_save', { default: 'Save' })
		: $format('button_next', { default: 'Next' });

	let removeContainerEventListener = () => {};

	const scrollSelectedCardToView = () => {
		removeContainerEventListener();

		scrolledToView = false;
		let elementRef = null;
		let wizardStepIndex = 0;
		// this is for the first step, when the user open the modal and the selected device is bellow the screen view and view is auto scroll to the selected device
		// if the modal  is not on the first step, the view will be auto scroll to the top of the modal
		if (activeStep.activeIndex === 0 && allSensorsRefs[selectedSensor?.id] && !scrolledToView) {
			wizardStepIndex = 0;
			elementRef = allSensorsRefs[selectedSensor.id];
		} else {
			wizardStepIndex = activeStep.activeIndex;
			elementRef = null;
		}

		if (!elementRef) {
			modalRef.scrollToTop();
		} else {
			removeContainerEventListener = scrollToElement(
				elementRef,
				wizardItemsContainerRef,
				activeStep.activeIndex,
				wizardStepIndex,
			);
		}

		scrolledToView = true;
	};

	const onClickWizardItem = (e, index: number) => {
		wizardItems = wizardItems.map((item, i) => {
			if (i === index) {
				activeStep = {
					...item,
					activeIndex: i,
				};

				return {
					...item,
					active: true,
				};
			}

			return {
				...item,
				active: false,
			};
		});

		scrollSelectedCardToView();
	};

	let hideFooter = false;
	// $: is_valid =
	// 	alarm_object?._meta?.name !== '' &&
	// 	alarm_object?._meta?.image !== '' &&
	// 	alarm_object?.do?.length > 0 &&
	// 	alarm_object?.if?.or.length > 0;

	/**
	 * Compute per-step validity for the wizard navigation guards.
	 *
	 * @param alarm_object - The alarm being created or edited.
	 * @param presenceSelected - Whether any presence device is currently selected.
	 *   When false, step 1 (Zones) is unconditionally valid so that lingering
	 *   presence conditions in alarm_object cannot block subsequent tabs.
	 * @returns A map of step index → boolean validity.
	 */
	const getWizardStepValidation = (
		alarm_object: IAlarmZone,
		presenceSelected: boolean,
		presenceSensors: { id?: string }[] = [],
	) => {
		const alarmMessage =
			typeof alarm_object?.do[0].do?.blk?.[0]?.msg !== 'undefined'
				? alarm_object?.do[0].do?.blk?.[0]?.msg
				: alarm_object?.do[0].msg;

		const lastStepValidation =
			alarm_object?._meta?.name?.trim() !== '' &&
			(alarm_object?._meta?.image !== '' || alarm_object?._meta?.backgroundColor !== '');

		const presenceHexIds = !presenceSelected
			? []
			: [
					...new Set(
						presenceSensors.map((s) => s?.id).filter((id): id is string => Boolean(id)),
					),
				].filter((id) => isShellyPresence($devices[id]?.type));

		const allPresenceZonesValid =
			presenceHexIds.length === 0 ||
			presenceHexIds.every((hexId) => {
				const radarActive = readDeviceSettings(real_id(hexId))?.presence?.enable !== false;
				if (!radarActive) return true;
				const decimal = hexId.toLowerCase().startsWith('x')
					? hexId
					: String(parseInt(hexId, 16));
				return (alarm_object?.if?.or ?? []).some((e) => {
					const parts = e?.eq?.[0]?.rr?.split(':') ?? [];
					return (
						parts[0] === 'dev' &&
						parts[1] === decimal &&
						/^prsn_st4\d+$/.test(parts[2] ?? '')
					);
				});
			});

		return {
			0:
				alarm_object?.if?.or?.length > 0 ||
				alarm_object?.if?.and?.find((el) => el?.or?.some((e) => !e.indailyint)),
			1: !presenceSelected || allPresenceZonesValid,
			2:
				alarm_object?.if?.or ||
				alarm_object?.if?.and.find((el) => el?.indailyint) ||
				alarm_object?.if?.and?.find((el) => el?.or?.every((e) => e.indailyint)),
			3:
				alarm_object?.do?.length > 0 &&
				alarm_object?._enabled !== 'undefined' &&
				alarmMessage !== '' &&
				alarm_object?.do[0].value !== '',
			4: alarm_object?._enable_delay >= 0 && alarm_object?.do[0]?.wait >= 0,
			5: lastStepValidation,
			6: lastStepValidation,
		};
	};

	let wizardStepValidation = getWizardStepValidation(
		alarm_object,
		hasPresenceSelected,
		selectedPresenceSensors,
	);

	const clearData = () => {
		alarm_object = _deepClone(alarm_object_prototype);
		id = undefined;
	};

	const onCloseAddEditModal = () => {
		clearData();
		showAddEditAlarmModal = false;
	};

	const save_alarm = async (alarm) => {
		try {
			let res: any = {};
			if (alarm._id) {
				//edit alarm
				res = await postReq('/scene/edit', {
					id,
					scene_script: JSON.stringify(alarm_object),
				});
			} else {
				res = await postReq('scene/add', {
					scene_script: JSON.stringify(alarm_object),
				});

				if (res.isok) {
					alarm = {
						...alarm,
						_meta: {
							...(alarm._meta ?? {}),
							new: true, // When adding a new item, set the temporary prop `new` to show the pulse animation
						},
					};
				}
			}
			if (res.isok && res?.data?.scene_id) {
				alarm._id = res.data.scene_id;
				alarm.id = res.data.scene_id;
			} else {
				alarm.id = alarm._id;
			}
			return alarm;
		} catch (error) {
			return error;
		}
	};

	const handleSave = () =>
		runSave(async () => {
			try {
				let _copy_alarm: any = _deepClone(alarm_object);
				let hasActivityLogCopyCheck = _copy_alarm.do.some(
					({ notify }) => notify === 'event_log',
				);

				(alarm_object._meta as any).sensors = [
					...new Set<string>(
						(_copy_alarm?.if?.or ?? []).map((el: any) =>
							shellyId16(el?.eq[0]?.rr?.split(':')[1]),
						),
					),
				];

				alarm_object._meta.active = 0;
				alarm_object._meta.name = alarm_object?._meta?.name?.trim(); //removing white spaces before saving

				if (
					typeof _copy_alarm?._meta?.position === 'number' &&
					_copy_alarm?._meta.position >= 0
				) {
					alarm_object._meta.position = _copy_alarm?._meta?.position;
				}

				if (!$alarms[alarm_object?._id]) {
					await orderItemsInRoom(alarm_object, 'alarms');
					alarm_object._meta.position = 0; //New items should be added at the first position to eliminate the need to scroll when adding a new item
				}

				alarm_object._meta.triggered = false;

				if (isActivityLogCheck && !hasActivityLogCopyCheck) {
					alarm_object?.do.push({
						notify: 'event_log',
						_gui_type: 'notification',
					});
				} else if (!isActivityLogCheck && hasActivityLogCopyCheck) {
					alarm_object.do = alarm_object?.do.filter((object) => {
						return object.notify !== 'event_log';
					});
				}

				if (alarm_object.do[0]?.scale === 'min') {
					alarm_object.do[0].wait = +alarm_object.do[0].wait * 60;
				}

				const alarm = await save_alarm(alarm_object);
				if (!$edit) {
					scenes.addScene(alarm);

					onItemSaved(alarm, 'alarms');
					dispatchAlarmSaved('alarm-saved', alarm);
				} else if ($edit) {
					scenes.editScene(alarm);
					dispatchAlarmSaved('alarm-saved', alarm);
				}
				onCloseAddEditModal();
			} catch (err) {
				toast.open({ type: 'danger', title: 'Something went wrong' });
			}
		});

	const onCloseEditModal = () => {
		wizardItems = resetWizardItems(wizardItems);
		activeStep = wizardItems[0];
		onCloseAddEditModal();
		dispatch('close');
	};

	const onClickPrevNextButton = (type: 'prev' | 'next') => {
		let result = updateWizardStepOnPrevNext(type, wizardItems, activeStep);
		// Skip the Zones step (index 1) when no presence device is selected
		if (result.activeWizardStep.activeIndex === 1 && !hasPresenceSelected) {
			result = updateWizardStepOnPrevNext(type, result.wizardItems, result.activeWizardStep);
		}
		wizardItems = result.wizardItems;
		activeStep = { ...result.activeWizardStep };
		scrollSelectedCardToView();
	};
	const onFinishCardEdit = async () => {
		if (
			alarm_object?.do?.[0]?.do?.blk[0]?.msg.trim() !== '' ||
			alarm_object?.do[0].msg.trim() !== ''
		) {
			await handleSave();
			onCloseEditModal();
		} else {
			inputAlarmMessegRef.focus();
		}
	};

	function checkActiveDevices(shouldCheckDevices = true) {
		if (!shouldCheckDevices) return;

		if (alarm_object?.if.hasOwnProperty('or')) {
			if (alarm_object?.if?.or?.length > 0) {
				let filteredAlarmDevices = alarm_object?.if?.or.filter((device, i) => {
					// get the device id
					let _deviceId = device?.eq[0]?.rr?.split(':')[1];

					// if the _deviceId is decimal then put it into shellyId16 and returns the device no matter if the id is decimal or not
					let deviceByShelly = _deviceId ? $devices[shellyId16(_deviceId)] : undefined;
					// get real id of the device
					let deviceIdInAlarm = deviceByShelly?.id;
					//check if device exist in the list with all devices
					let deviceExist =
						Object.keys($devices).find((devId) => devId === deviceIdInAlarm) !== undefined;

					return deviceExist;
				});

				alarm_object.if.or = [...filteredAlarmDevices];
			}
		} else if (alarm_object?.if.hasOwnProperty('and')) {
			if (alarm_object?.if?.and.length > 0) {
				alarm_object?.if?.and.map((device) => {
					console.log('%c device AND', 'color: orange', device);
				});
			}
		}
	}

	onMount(() => {
		// When param is true removes missing devices from alarm and doesn't show them.
		checkActiveDevices(false);
	});

	beforeUpdate(() => {
		/**
		 * Move wizardStepValidation here,
		 * because after removing context alarm_object is no more reactive an the check never updates.
		 * So to trigger check again after selecting device, do check on every change
		 */
		wizardStepValidation = getWizardStepValidation(
			alarm_object,
			hasPresenceSelected,
			selectedPresenceSensors,
		);
	});

	$: if (!initiallyLoadedEditWizard && allSensorsRefs[selectedSensor?.id] && wizardContainerRef) {
		removeContainerEventListener();

		removeContainerEventListener = scrollToElement(
			allSensorsRefs[selectedSensor?.id],
			wizardContainerRef,
			0,
			0,
		);
		initiallyLoadedEditWizard = true;
	}
</script>

<Modal
	on:close={() => {
		if (JSON.stringify(alarm_object) !== JSON.stringify(clonedAlarmObject)) {
			showExitConfirmationModal = true;
			exitConfirmationModalState = false;
		} else {
			hideFooter = false;
			onCloseEditModal();
		}
	}}
	showModal={showAddEditAlarmModal}
	title={alarm_object._id
		? $format('prompt_title_edit_alarm', {
				default: 'Edit alarm',
			})
		: $format('prompt_title_add_alarm', {
				default: 'Add alarm',
			})}
	hideSlotFooter={hideFooter}
	bind:this={modalRef}
	removeScrollPadding
>
	<svelte:fragment slot="toolbar">
		<Wizard
			activeStepNumber={activeStep.stepNumber}
			stepsTotal={wizardItems.length}
			activeStepTitle={activeStep.title}
			slot="toolbar"
		>
			{#each wizardItems as item, i (i)}
				{#if i !== 1 || hasPresenceSelected}
					<WizardItem
						on:click={(e) => {
							onClickWizardItem(e, i);
						}}
						active={item.active}
						title={item.title}
						disabled={typeof wizardStepValidation === 'object'
							? item.stepNumber > activeStep.stepNumber &&
								(!wizardStepValidation[activeStep.activeIndex] ||
									Array.from({ length: i }, (_, k) => k).some(
										(k) => !wizardStepValidation[k],
									))
							: false}
						showMoreDots={item.showMoreDots}
						indicatorColor={$showNewLayout ? 'color-2' : undefined}
					/>
				{/if}
			{/each}
		</Wizard>
	</svelte:fragment>

	<div class:flex-overflow-hidden={hideFooter} slot="content">
		<WizardContainer
			bind:wizardContainerRef
			bind:wizardItemsContainerRef
			activeStepIndex={activeStep.activeIndex}
			adaptiveHeight
			fullHeight={hideFooter}
		>
			{#each wizardItems as item, i (i)}
				<!-- DO NOT REMOVE COMMENT: Padding references -->
				<WizardContent
					active={activeStep.activeIndex === i}
					class={classMap({
						'pt-s pl-0 pr-0': true,
						'pb-s': i !== 4,
					})}
				>
					{#if i === 0}
						<SelectDevicesStep
							bind:alarm_object
							bind:selectedSensor
							bind:allSensorsRefs
							bind:hasPresenceSelected
							bind:selectedPresenceSensors
						/>
					{:else if i === 1}
						<SelectZonesStep bind:alarm_object {selectedPresenceSensors} />
					{:else if i === 2}
						<ActiveTimeStep
							bind:alarm_object
							on:hide_footer={(e) => (hideFooter = e.detail.hideFooter)}
						/>
					{:else if i === 3}
						<MoreStep bind:inputAlarmMessegRef bind:alarm_object bind:isActivityLogCheck />
					{:else if i === 4}
						<DelaysStep
							bind:alarm_object
							bind:isActivityLogCheck
							bind:delayArmActionError
							bind:delayActionError
						/>
					{:else if i === 5}
						<MainStep bind:alarm_object />
					{:else}
						<OverviewStep bind:alarm={alarm_object} />
					{/if}
				</WizardContent>
			{/each}
		</WizardContainer>
	</div>

	<div slot="footer" class="d-flex justify-content-end">
		{#if !isFirstStep}
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				icon="fas fa-chevron-left"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => {
					onClickPrevNextButton('prev');
				}}
			>
				{previousButton}
			</Button>
		{/if}
		<Button
			class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
			icon={isLastStepWizard ? 'fas fa-save' : ''}
			rightIcon={isLastStepWizard ? '' : 'fas fa-chevron-right'}
			disabled={typeof wizardStepValidation === 'object'
				? !wizardStepValidation[activeStep.activeIndex]
				: isLastStepWizard}
			loading={isLastStepWizard && $isSaving}
			responsiveSize={{
				sm: 'm',
			}}
			on:click={() => {
				if (isLastStepWizard) {
					onFinishCardEdit();
				} else {
					onClickPrevNextButton('next');
				}
			}}
		>
			{nextButton}
		</Button>
	</div>
</Modal>

<ExitConfirmationModal
	type={$format('the_alarm', { default: 'the alarm' })}
	{exitConfirmationModalState}
	{showExitConfirmationModal}
	showAddEditModal={showAddEditAlarmModal}
	on:close={(e) => {
		exitConfirmationModalState = e.detail.exitConfirmationModalState;
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
	}}
	on:confirm={(e) => {
		dispatch('close');
		showExitConfirmationModal = e.detail.showExitConfirmationModal;
		showAddEditAlarmModal = e.detail.showAddEditModal;
	}}
/>
